import {
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AcxInputLabel from "components/UI/AcxInputLabel";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import { observer } from "mobx-react";
import { PipelineProcessTypes } from "models/DataProcesses";
import React, { useEffect } from "react";
import { useQuill } from "react-quilljs";
import { FormFieldErrors } from "shared/types/FormErrors.type";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import useDataProcessingOptions from "../../hooks/useDataProcessingOptions";
import { Organization } from "../../types/Organization.type";
import useOrganizationUserNames from "../../hooks/useOrganizationUserNames";
import useExtendedMetadataFields from "../../hooks/useExtendedMetadataFields";
import ZeroCallsSettings from "./ZeroCallsSettings";
import KboUserAgreementSettings from "./KboUserAgreementSettings";

// Styled component for paddingRight
const PaddingRightGrid = styled(Grid)({
    paddingRight: "0.8rem!important",
});

const groupableLimitMaxValue = 10000;
const groupableLimitMinValue = 1;

type Props = {
    updateOrg: (
        val: string | string[] | boolean | number,
        prop: keyof Organization,
    ) => void;
    isLoading: boolean;
    org?: Organization | null;
    fieldErrors?: FormFieldErrors;
    formErrors?: string[];
    updateContainerName: (val?: string | number) => void;
};

const timeoutOptions = [
    {
        label: "2 Minutes",
        value: 2 * 60 * 1000,
    },
    {
        label: "5 Minutes",
        value: 5 * 60 * 1000,
    },
    {
        label: "10 Minutes",
        value: 10 * 60 * 1000,
    },
    {
        label: "15 Minutes",
        value: 15 * 60 * 1000,
    },
    {
        label: "30 Minutes",
        value: 30 * 60 * 1000,
    },
    {
        label: "1 Hour",
        value: 60 * 60 * 1000,
    },
    {
        label: "4 Hours",
        value: 60 * 60 * 1000 * 4,
    },
];

const processOptions = [
    {
        label: "Automatic",
        value: PipelineProcessTypes.Automatic,
    },
    {
        label: "On Demand",
        value: PipelineProcessTypes.OnDemand,
    },
];

const processOptionsTooltips = {
    Automatic:
        "Automatic: Interaction files are processed, transcribed, and receive classifier results upon receipt of the interaction",
    "On Demand":
        "On Demand: Interaction files are processed, transcribed, and receive classifier results upon sampling the interaction",
};

const dataProcessingTooltips = {
    Transcription: "Transcription: Enables transcription",
    Classification: "Classification: Enables MLs and RBCs to collect results",
    HumanAnalysis: "HumanAnalysis: Enables BOTF evaluations for a tenant",
    AddressRedaction: "AddressRedaction: Redacts Addresses from interactions",
    // CC not defined, default to label
    NameRedaction: "NameRedaction: Redacts Names from interactions",
    // Date not defined, default to label
    // Phone not defined, default to label
    Redaction:
        "Redaction: Enables redaction for a tenant. Must be selected in order for any specific redaction options to work.",
    MiddleOfTheFunnel:
        "MiddleOfTheFunnel: Enables MOTF evaluations for a tenant",
    VoiceRedaction:
        'VoiceRedaction: Obfuscates voices in audio interactions by allowing the tenant to specify level of obfuscation with the "Voice Redaction Factor" scale',
    SSNRedaction:
        "SSNRedaction: Redacts Social Security Numbers from interactions",
    LooseMetadataCoupling:
        "LooseMetadataCoupling: Enables a client to have metadata processing specs at the organization level while also processing call files with no metadata. Files will continue processing to success even without metadata being added.",
    ClassifierValidation:
        "ClassifierValidation: Not Used - Enables the 'Listen For' module on Evaluations",
    RaiseAudioByOctave:
        "RaiseAudioByOctave: Obfuscates voices in audio interactions by decreasing the pitch of all voices by one octave",
    LowerAudioByOctave:
        "LowerAudioByOctave: Obfuscates voices in audio interactions by increasing the pitch of all voices by one octave",
    BypassAgentOsmAssignment:
        "BypassAgentOSMAssignment : Enabling this processing option tells the pipeline to NOT use Agent Hierarchy Relationships to assign a Hierarchy to an AMD",
    EnableScreenRecordings:
        "EnableScreenRecordings : Enabling this is what allows Screen Recordings to exist for an Organization",
    BypassScreenRecordingRequiredForEvaluation:
        "BypassScreenRecordingRequiredForEval : Enabling this allows for evaluations to exist without a corresponding ScreenRecording being found - cannot be used without EnableScreenRecordings",
};

// tooltip values to match labels for org details
const orgDetailTooltips = {
    name: "Name of the organization",
    dbConnectionString:
        "String that connects the clients database to the Authenticx Platform",
    pgBouncerConnectionString:
        "PGBouncer connection string that connects the clients database to the Authenticx Platform",
    dbVersionString: "Clients version database to the Authenticx Platform",
    blobContainer: `Name of the blob folder within the Storage Account "interactionfilesinstage" that call files will land in when initially uploaded to the Authenticx Platform (either via SFTP or API)`,
    pgpKeyName: "The key Authenticx uses to decrypt any PGP files received",
    maxIdleDuration:
        "How long a browser window can sit idle before being automatically logged out",
    motfAnalystName: `The name in the Analyst field for MOTF evaluations. By default, this will be "AcxMachine"`,
    searchService:
        "Name of the index in the Azure service that enables the Index search",
    searchServiceIndex:
        "The index file that indexes transcriptions in the indexable directory",
    eddyHourlyRate: "The hourly rate used to calculate Eddy Cost",
    agentAck:
        "Number of business days before agent can no longer acknowledge the evaluation. Weekends and standard US Holidays will not be counted as business days",
    voiceRedaction: `The level of redaction that is applied to Audio Interactions if "Redaction" and "Voice Redaction" are enabled under Data Processing Options`,
    enhancedModuleView:
        "Enables an additional column in the Interactions Domain that shows which modules within an evaluation are still incomplete.",
    enhancedPermissions:
        "Enables enhanced role permissions to provide additional flexibility in role configuration. This should always be enabled.",
    unredactedTranscriptionVisibility:
        "Controls whether unredacted transcription content is visible for this organization.",
    requireMfa:
        "Require password-based users in this organization to complete MFA setup and enter an email or authenticator app code during login.",
    emailFooterPlain:
        "A custom footer added to all emails sent from the Authenticx Platform to the clients users. Plain text should be utilized when the client has plain text emails enabled for their employees.",
    emailFooterHtml:
        "A custom footer added to all emails sent from the Authenticx Platform to the clients users. HTML is the default format for most emails.",
    groupableLimit: `This is the max number of unique values a groupable metadata field can have. Maximum of ${groupableLimitMaxValue}`,
    metadataPatientId:
        "The extended metadata field that holds the patient ID for this organization's interactions",
};

const OrganizationDetails: React.FC<Props> = observer(
    ({
        updateOrg,
        isLoading,
        org,
        fieldErrors,
        formErrors,
        updateContainerName,
    }) => {
        const theme = useTheme();
        const authStore = useStore(AuthStore);
        const { data: dpOptions, isLoading: dpIsLoading } =
            useDataProcessingOptions();

        const { users, isLoading: usersLoading } = useOrganizationUserNames(
            org!.id! as string,
        );

        const {
            fields: extendedMetadataFields,
            isLoading: fieldsLoading,
            hasLoaded: fieldsHaveLoaded,
        } = useExtendedMetadataFields(org!.id! as string);

        const activePatientIdField = extendedMetadataFields.find(
            (field) => field.fieldName === org?.metadataPatientId,
        );
        const isPatientIdFieldInactive =
            !!org?.metadataPatientId &&
            fieldsHaveLoaded &&
            !activePatientIdField;

        // Still shows a field that's no longer active, so it isn't silently kept.
        const selectedPatientIdField = org?.metadataPatientId
            ? activePatientIdField ?? {
                  label: org.metadataPatientId,
                  fieldName: org.metadataPatientId,
              }
            : undefined;

        const handleTimeoutChange = (arg: { label: string; value: number }) => {
            if (org) {
                updateOrg(arg?.value as any, "timeoutDuration");

                if (authStore.orgStore?.selectedOrganization) {
                    authStore.orgStore.selectedOrganization.timeoutDuration =
                        arg?.value;
                }
            }
        };

        const modules = {
            toolbar: [
                ["bold", "italic", "underline", "strike", "code"],
                [{ align: [] }],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ indent: "-1" }, { indent: "+1" }],
                ["link", "image"],
                [{ color: [] }, { background: [] }],
                [{ size: ["small", false, "large", "huge"] }],
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ],
        };

        const { quill, quillRef } = useQuill({
            theme: "snow", // required
            modules,
        });

        const handleProcessOptionChange = (arg: {
            label: string;
            value: number;
        }) => {
            if (org) {
                updateOrg(arg?.value as any, "pipelineProcessingOption");
            }
        };

        const [voiceRedactionFactorValue, setVoiceRedactionFactorValue] =
            React.useState<number | undefined>(org?.voiceRedactionFactor);

        useEffect(() => {
            if (
                (org?.voiceRedactionFactor ||
                    org?.voiceRedactionFactor === 0) &&
                (voiceRedactionFactorValue === undefined || null) &&
                org?.id
            )
                setVoiceRedactionFactorValue(org?.voiceRedactionFactor);
            else if (org?.id && !org?.voiceRedactionFactor)
                setVoiceRedactionFactorValue(0);
        }, [org?.id, org?.voiceRedactionFactor, voiceRedactionFactorValue]);

        useEffect(() => {
            if (quill && org?.customEmailFooterHtml) {
                quill.clipboard.dangerouslyPasteHTML(
                    org?.customEmailFooterHtml,
                );
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [org?.id, quill]);

        useEffect(() => {
            if (quill) {
                quill.on("text-change", () => {
                    updateOrg(quill.root.innerHTML, "customEmailFooterHtml");
                });
            }
        }, [quill, updateOrg]);

        return (
            <Paper
                elevation={1}
                sx={{
                    marginTop: theme.spacing(2),
                    paddingTop: theme.spacing(4),
                    paddingBottom: theme.spacing(4),
                    paddingLeft: theme.spacing(4),
                    paddingRight: theme.spacing(4),
                    width: "100%",
                    overflowY: "auto",
                }}
            >
                <Typography variant="h5">Organization Details</Typography>
                <Typography variant="caption">
                    {org?.id ? "Edit organization details" : "Add organization"}
                </Typography>
                <Grid container spacing={1} style={{ paddingTop: "12px" }}>
                    <Grid container item spacing={1} wrap="wrap">
                        <Tooltip title={orgDetailTooltips.name} arrow>
                            <Grid item xs={12}>
                                <AcxMainTextField
                                    skeleton={isLoading}
                                    labelText="Name"
                                    id="name"
                                    value={org?.name}
                                    isDisabled={
                                        org?.id ? org?.name === "Authcx" : false
                                    }
                                    onChange={(e) => {
                                        if (
                                            org?.id &&
                                            e.target.value === "Authcx"
                                        ) {
                                            e.target.value = "";
                                        }
                                        updateOrg(e.target.value, "name");
                                    }}
                                    error={!!fieldErrors?.name}
                                    helperText={fieldErrors?.name}
                                    showAllErrors={true}
                                    onBlur={updateContainerName}
                                />
                            </Grid>
                        </Tooltip>
                        <Tooltip
                            title={orgDetailTooltips.dbConnectionString}
                            arrow
                        >
                            <Grid item xs={9}>
                                <AcxMainTextField
                                    skeleton={isLoading}
                                    labelText="Database Connection"
                                    id="databaseConnectionString"
                                    value={org?.databaseConnectionString}
                                    onChange={(e) =>
                                        updateOrg(
                                            e.target.value,
                                            "databaseConnectionString",
                                        )
                                    }
                                    error={
                                        !!fieldErrors?.databaseConnectionString
                                    }
                                    showAllErrors={true}
                                />
                            </Grid>
                        </Tooltip>
                        <Tooltip
                            title={orgDetailTooltips.dbVersionString}
                            arrow
                        >
                            <Grid item xs={3}>
                                <AcxMainTextField
                                    skeleton={isLoading}
                                    labelText="Database Version"
                                    id="databaseVersionString"
                                    value={org?.databaseVersion}
                                    onChange={(e) =>
                                        updateOrg(
                                            e.target.value,
                                            "databaseVersion",
                                        )
                                    }
                                    error={!!fieldErrors?.databaseVersion}
                                    showAllErrors={true}
                                />
                            </Grid>
                        </Tooltip>
                        <Tooltip
                            title={orgDetailTooltips.pgBouncerConnectionString}
                            arrow
                        >
                            <Grid item xs={9}>
                                <AcxMainTextField
                                    skeleton={isLoading}
                                    labelText="PGBouncer Connection String"
                                    id="pgBouncerConnectionString"
                                    value={org?.pgBouncerConnectionString}
                                    onChange={(e) =>
                                        updateOrg(
                                            e.target.value,
                                            "pgBouncerConnectionString",
                                        )
                                    }
                                    error={
                                        !!fieldErrors?.pgBouncerConnectionString
                                    }
                                    showAllErrors={true}
                                />
                            </Grid>
                        </Tooltip>
                        <Grid item xs={6}>
                            <Tooltip
                                title={orgDetailTooltips.motfAnalystName}
                                arrow
                            >
                                <Grid
                                    item
                                    xs={12}
                                    style={{ paddingTop: "4px" }}
                                >
                                    <AcxMainTextField
                                        skeleton={isLoading}
                                        onChange={(e) =>
                                            updateOrg(
                                                e.target.value,
                                                "middleOfTheFunnelAnalystName",
                                            )
                                        }
                                        id="middleOfTheFunnelAnalystName"
                                        labelText="Middle of the Funnel Analyst Name"
                                        value={
                                            org?.middleOfTheFunnelAnalystName
                                        }
                                        error={
                                            !!fieldErrors?.middleOfTheFunnelAnalystName
                                        }
                                        helperText={
                                            fieldErrors?.middleOfTheFunnelAnalystName
                                        }
                                        showAllErrors={true}
                                        infoText="The name of the analyst shown for MOTF evaluations. Sets Defaults to Acx Machine if not specified."
                                    />
                                </Grid>
                            </Tooltip>
                        </Grid>
                        <Tooltip title={orgDetailTooltips.blobContainer} arrow>
                            <Grid item xs={6}>
                                <AcxMainTextField
                                    skeleton={isLoading}
                                    onChange={(e) =>
                                        updateOrg(
                                            e.target.value,
                                            "blobContainer",
                                        )
                                    }
                                    id="blobContainer"
                                    labelText="Blob Container"
                                    value={org?.blobContainer}
                                    error={!!fieldErrors?.blobContainer}
                                    helperText={fieldErrors?.blobContainer}
                                    showAllErrors={true}
                                    // readonly={!!org?.id}
                                />
                            </Grid>
                        </Tooltip>
                        <Tooltip title={orgDetailTooltips.pgpKeyName} arrow>
                            <Grid item xs={6}>
                                <AcxMainTextField
                                    skeleton={isLoading}
                                    onChange={(e) =>
                                        updateOrg(e.target.value, "pgpKeyName")
                                    }
                                    id="pgpKeyName"
                                    labelText="Pgp Key Name"
                                    value={org?.pgpKeyName}
                                    error={!!fieldErrors?.pgpKeyName}
                                    helperText={fieldErrors?.pgpKeyName}
                                    showAllErrors={true}
                                />
                            </Grid>
                        </Tooltip>
                        <PaddingRightGrid item xs={6}>
                            <AcxSelect
                                inputLabel="Pipeline Processing Option"
                                defaultValue={processOptions.find((option) => {
                                    return (
                                        option.value ===
                                        org?.pipelineProcessingOption
                                    );
                                })}
                                id="pipeline-processing-option-selector"
                                labelField="label"
                                valueField="value"
                                tooltips={processOptionsTooltips} // hardcoded
                                options={processOptions} // hardcoded
                                onChange={handleProcessOptionChange}
                                placeholder="Select Pipeline Processing Option"
                                fullWidth={true}
                            />
                        </PaddingRightGrid>
                        <PaddingRightGrid item xs={6}>
                            <AcxSelectMulti
                                id="dataProcessingOptions"
                                options={dpOptions ? dpOptions : []}
                                valueField="id"
                                labelField="processingOption"
                                tooltips={dataProcessingTooltips} // hardcoded
                                inputLabel="Data Processing Options"
                                skeleton={isLoading || dpIsLoading}
                                defaultValue={org?.dataProcessingOptions?.map(
                                    (opt) =>
                                        dpOptions?.find(
                                            (opt2) =>
                                                opt === opt2.processingOption,
                                        ),
                                )}
                                onChange={(e) =>
                                    updateOrg(
                                        e
                                            ? e.map(
                                                  (opt) =>
                                                      opt?.processingOption ||
                                                      "",
                                              )
                                            : [],
                                        "dataProcessingOptions",
                                    )
                                }
                                containerHeight="auto"
                                fullWidth={true}
                            />
                        </PaddingRightGrid>
                        <PaddingRightGrid item xs={6}>
                            <Tooltip
                                title={orgDetailTooltips.maxIdleDuration}
                                arrow
                            >
                                <Grid item xs={12}>
                                    <AcxSelect
                                        inputLabel="Max Idle Duration"
                                        fullWidth={true}
                                        defaultValue={timeoutOptions.find(
                                            (option) => {
                                                return (
                                                    option.value ===
                                                    org?.timeoutDuration
                                                );
                                            },
                                        )}
                                        id="idle-duration-timeout-selector"
                                        labelField="label"
                                        valueField="value"
                                        options={timeoutOptions}
                                        onChange={handleTimeoutChange}
                                        placeholder="Select Maximum Idle Time"
                                    />
                                </Grid>
                            </Tooltip>
                        </PaddingRightGrid>
                        {/* <Tooltip title={orgDetailTooltips.searchService} arrow>
                            <Grid item xs={6}>
                                <AcxMainTextField
                                    skeleton={isLoading}
                                    onChange={(e) =>
                                        updateOrg(
                                            e.target.value,
                                            "searchService",
                                        )
                                    }
                                    id="searchService"
                                    labelText="Search Service"
                                    value={org?.searchService}
                                    error={!!fieldErrors?.searchService}
                                    helperText={fieldErrors?.searchService}
                                    showAllErrors={true}
                                />
                            </Grid>
                        </Tooltip> */}
                        {/* <Tooltip
                            title={orgDetailTooltips.searchServiceIndex}
                            arrow
                        >
                            <Grid item xs={6}>
                                <AcxMainTextField
                                    skeleton={isLoading}
                                    onChange={(e) =>
                                        updateOrg(
                                            e.target.value,
                                            "searchServiceIndex",
                                        )
                                    }
                                    id="searchServiceIndex"
                                    labelText="Search Service Index"
                                    value={org?.searchServiceIndex}
                                    error={!!fieldErrors?.searchServiceIndex}
                                    helperText={fieldErrors?.searchServiceIndex}
                                    showAllErrors={true}
                                />
                            </Grid>
                        </Tooltip> */}
                        <Tooltip title={orgDetailTooltips.eddyHourlyRate} arrow>
                            <Grid item xs={6}>
                                <AcxMainTextField
                                    skeleton={isLoading}
                                    onChange={(e) =>
                                        updateOrg(
                                            e.target.value,
                                            "eddyHourlyRate",
                                        )
                                    }
                                    type="number"
                                    id="eddyHourlyRate"
                                    labelText="Eddy Hourly Rate"
                                    value={org?.eddyHourlyRate}
                                    error={!!fieldErrors?.eddyHourlyRate}
                                    helperText={fieldErrors?.eddyHourlyRate}
                                    showAllErrors={true}
                                />
                            </Grid>
                        </Tooltip>
                        <Tooltip title={orgDetailTooltips.agentAck} arrow>
                            <Grid item xs={6}>
                                <AcxMainTextField
                                    skeleton={isLoading}
                                    onChange={(e) =>
                                        updateOrg(
                                            e.target.value,
                                            "evalAcknowledgeDays",
                                        )
                                    }
                                    type="number"
                                    id="evaluation-acknowledge-days-input"
                                    labelText="Agent Evaluation Acknowledgement"
                                    placeholderText="Enter number of business days…"
                                    value={org?.evalAcknowledgeDays}
                                    error={!!fieldErrors?.evalAcknowledgeDays}
                                    helperText={
                                        fieldErrors?.evalAcknowledgeDays
                                    }
                                    showAllErrors={true}
                                    insertBlankIfNumber
                                />
                            </Grid>
                        </Tooltip>
                        <Tooltip title={orgDetailTooltips.groupableLimit} arrow>
                            <Grid item xs={6}>
                                <AcxMainTextField
                                    skeleton={isLoading}
                                    onChange={(e) => {
                                        const val = Math.min(
                                            Math.max(
                                                Number(e.target.value),
                                                groupableLimitMinValue,
                                            ),
                                            groupableLimitMaxValue,
                                        );
                                        updateOrg(
                                            String(val),
                                            "MaxDistinctGroupableClassifierOutputs",
                                        );
                                    }}
                                    id="MaxDistinctGroupableClassifierOutputs"
                                    labelText="Groupable Limit"
                                    value={
                                        org?.MaxDistinctGroupableClassifierOutputs
                                    }
                                    error={
                                        !!fieldErrors?.MaxDistinctGroupableClassifierOutputs
                                    }
                                    helperText={
                                        fieldErrors?.MaxDistinctGroupableClassifierOutputs
                                    }
                                    showAllErrors={true}
                                    type="number"
                                    inputProps={{
                                        min: groupableLimitMinValue,
                                        max: groupableLimitMaxValue,
                                    }}
                                />
                            </Grid>
                        </Tooltip>
                        <PaddingRightGrid item xs={6}>
                            <Tooltip
                                title={orgDetailTooltips.metadataPatientId}
                                arrow
                            >
                                <Grid item xs={12}>
                                    <AcxSelect
                                        inputLabel="Metadata Patient ID"
                                        id="metadata-patient-id-selector"
                                        options={extendedMetadataFields}
                                        labelField="label"
                                        valueField="fieldName"
                                        defaultValue={selectedPatientIdField}
                                        onChange={(field) =>
                                            updateOrg(
                                                field?.fieldName ?? "",
                                                "metadataPatientId",
                                            )
                                        }
                                        isLoading={fieldsLoading}
                                        skeleton={isLoading}
                                        isClearable
                                        placeholder="Select Extended Metadata Field"
                                        helperText={
                                            isPatientIdFieldInactive
                                                ? `Currently pointing to "${org?.metadataPatientId}", which isn't an active extended metadata field. Select an active field or clear it.`
                                                : undefined
                                        }
                                        fullWidth={true}
                                    />
                                </Grid>
                            </Tooltip>
                        </PaddingRightGrid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={(e) =>
                                            updateOrg(
                                                e.target.checked,
                                                "isActive",
                                            )
                                        }
                                        id="isActive"
                                        name={"isActive"}
                                        checked={org?.isActive || false}
                                        disabled={isLoading}
                                    />
                                }
                                label="Is Active"
                            />
                            {authStore.isUserUltra() && (
                                <>
                                    <Tooltip
                                        title={
                                            orgDetailTooltips.enhancedPermissions
                                        }
                                        arrow
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    onChange={(e) =>
                                                        updateOrg(
                                                            e.target.checked,
                                                            "enhancedPermissionStatus",
                                                        )
                                                    }
                                                    id="enhancedPermissionStatus"
                                                    name={
                                                        "enhancedPermissionStatus"
                                                    }
                                                    checked={
                                                        org?.enhancedPermissionStatus ||
                                                        false
                                                    }
                                                    disabled={isLoading}
                                                />
                                            }
                                            label="Enhanced Permissions"
                                        />
                                    </Tooltip>
                                    <Tooltip
                                        title={
                                            orgDetailTooltips.unredactedTranscriptionVisibility
                                        }
                                        arrow
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    onChange={(e) =>
                                                        updateOrg(
                                                            e.target.checked,
                                                            "unredactedTranscriptionVisibility",
                                                        )
                                                    }
                                                    id="unredactedTranscriptionVisibility"
                                                    name={
                                                        "unredactedTranscriptionVisibility"
                                                    }
                                                    checked={
                                                        org?.unredactedTranscriptionVisibility ||
                                                        false
                                                    }
                                                    disabled={isLoading}
                                                />
                                            }
                                            label="Unredacted Transcription Visibility"
                                        />
                                    </Tooltip>
                                </>
                            )}
                            <Tooltip
                                title={orgDetailTooltips.requireMfa}
                                arrow
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            onChange={(e) =>
                                                updateOrg(
                                                    e.target.checked,
                                                    "requireMfa",
                                                )
                                            }
                                            id="requireMfa"
                                            name={"requireMfa"}
                                            checked={org?.requireMfa || false}
                                            disabled={isLoading}
                                        />
                                    }
                                    label="Require MFA"
                                />
                            </Tooltip>
                            {org?.requireMfa && (
                                <Grid
                                    container
                                    sx={{
                                        pl: 4,
                                        gap: 1,
                                        alignItems: "center",
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                onChange={(e) =>
                                                    updateOrg(
                                                        e.target.checked,
                                                        "mfaEmailEnabled",
                                                    )
                                                }
                                                id="mfaEmailEnabled"
                                                name="mfaEmailEnabled"
                                                checked={
                                                    org?.mfaEmailEnabled ||
                                                    false
                                                }
                                                disabled={isLoading}
                                            />
                                        }
                                        label="Email Code"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                onChange={(e) =>
                                                    updateOrg(
                                                        e.target.checked,
                                                        "mfaAuthenticatorAppEnabled",
                                                    )
                                                }
                                                id="mfaAuthenticatorAppEnabled"
                                                name="mfaAuthenticatorAppEnabled"
                                                checked={
                                                    org?.mfaAuthenticatorAppEnabled ||
                                                    false
                                                }
                                                disabled={isLoading}
                                            />
                                        }
                                        label="Authenticator App"
                                    />
                                    {fieldErrors?.requireMfa && (
                                        <Grid item xs={12}>
                                            <AcxInputLabel
                                                sx={{
                                                    fontFamily:
                                                        theme.typography
                                                            .fontFamily,
                                                    color: theme.palette.error
                                                        .main,
                                                    fontSize: "12px",
                                                    lineHeight: "16px",
                                                }}
                                            >
                                                {fieldErrors.requireMfa}
                                            </AcxInputLabel>
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </Grid>
                        <ZeroCallsSettings
                            isLoading={isLoading}
                            org={org}
                            updateOrg={updateOrg}
                            fieldErrors={fieldErrors}
                            users={users}
                            usersLoading={usersLoading}
                            userId={authStore._user?.profile.sub || ""}
                            orgId={org?.id || ""}
                        />
                        {/* Authenticx sets the terms, not the organization's own admins. */}
                        {authStore.isUserUltra() && (
                            <KboUserAgreementSettings
                                isLoading={isLoading}
                                org={org}
                                updateOrg={updateOrg}
                                fieldErrors={fieldErrors}
                            />
                        )}
                        <Tooltip
                            title={orgDetailTooltips.emailFooterPlain}
                            arrow
                        >
                            <Grid item xs={6}>
                                <AcxMainTextField
                                    skeleton={isLoading}
                                    onChange={(e) =>
                                        updateOrg(
                                            e.target.value,
                                            "customEmailFooterPlainText",
                                        )
                                    }
                                    id="customEmailFooterPlainText"
                                    labelText="Email Footer (plain text)"
                                    value={org?.customEmailFooterPlainText}
                                    showAllErrors={true}
                                    multiline={true}
                                    rows={15}
                                />
                            </Grid>
                        </Tooltip>
                        <Tooltip
                            title={orgDetailTooltips.emailFooterHtml}
                            arrow
                        >
                            <Grid item xs={6} style={{ height: "271px" }}>
                                <AcxInputLabel labelText="Email Footer (HTML)" />
                                <div ref={quillRef} />
                            </Grid>
                        </Tooltip>

                        <Grid item xs={12}>
                            {formErrors?.map((err, i) => (
                                <AcxInputLabel
                                    key={i}
                                    sx={{
                                        fontFamily: "Inter", // make Inter due to italics
                                        color: theme.palette.error.main,
                                        fontSize: "12px",
                                        lineHeight: "16px",
                                    }}
                                >
                                    {err}
                                </AcxInputLabel>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>
        );
    },
);

export default OrganizationDetails;
